import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  message,
  Switch,
  Tag,
  Select,
  PageHeader,
  PageStack,
  Drawer,
} from "lib/ui";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  CheckOutlined,
} from "lib/icons";
import {
  Package,
  Layers,
  Grid3X3,
  X,
  ImageIcon,
  ChevronRight,
  Sparkles,
  Plus,
  FolderPlus,
  Pencil,
  Trash2,
  Check,
  ZoomIn,
  LayoutGrid,
  List,
  Tag as TagIcon,
  ChevronLeft,
} from "lucide-react";
import { AuthContext } from "../../../context/Context";
import ApiPath from "../../../hooks/ApiFetch";
import { cn } from "../../../lib/cn";
import PRODUCT_IMAGES, { IMAGE_CATEGORIES } from "./productImages";

const { Option } = Select;
const { TextArea } = Input;

const IMAGES_PER_PAGE = 48;

/* ─── Image Picker Modal ─── */
function ImagePickerModal({ open, onClose, onSelect, currentImage }) {
  const [search, setSearch] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [preview, setPreview] = useState(null);

  const filtered = useMemo(() => {
    let items = PRODUCT_IMAGES;
    if (activeCategory !== "All") {
      items = items.filter((img) => img.category === activeCategory);
    }
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (img) =>
          img.label.toLowerCase().includes(q) ||
          img.key.toLowerCase().includes(q)
      );
    }
    return items;
  }, [activeCategory, search]);

  const totalPages = Math.ceil(filtered.length / IMAGES_PER_PAGE);
  const paged = filtered.slice(
    (page - 1) * IMAGES_PER_PAGE,
    page * IMAGES_PER_PAGE
  );

  const changeCategory = (cat) => {
    setActiveCategory(cat);
    setPage(1);
  };

  const changeSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const catCounts = useMemo(() => {
    const counts = { All: PRODUCT_IMAGES.length };
    PRODUCT_IMAGES.forEach((img) => {
      counts[img.category] = (counts[img.category] || 0) + 1;
    });
    return counts;
  }, []);

  /* pagination range helper */
  const pageRange = useMemo(() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    let start = Math.max(2, page - 1);
    let end = Math.min(totalPages - 1, page + 1);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  }, [page, totalPages]);

  return (
    <Modal
      title={null}
      visible={open}
      onCancel={onClose}
      footer={null}
      width={960}
      bodyStyle={{ padding: 0 }}
      closable={false}
    >
      <div className="flex flex-col" style={{ height: "80vh", maxHeight: 720 }}>
        {/* ── header bar ── */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-brand-surface/80 to-white px-5 py-3">
          <div>
            <h3 className="text-base font-bold text-brand-dark">
              Image Library
            </h3>
            <p className="text-[11px] text-slate-500">
              {filtered.length} image{filtered.length !== 1 && "s"}
              {activeCategory !== "All" && ` in ${activeCategory}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md transition",
                  viewMode === "grid"
                    ? "bg-brand text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-600"
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md transition",
                  viewMode === "list"
                    ? "bg-brand text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-600"
                )}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* ── sidebar: categories ── */}
          <div className="hidden w-44 shrink-0 overflow-y-auto border-r border-slate-100 bg-slate-50/60 py-2 md:block">
            <p className="mb-1 px-3 text-[9px] font-bold uppercase tracking-widest text-slate-400">
              Categories
            </p>
            {IMAGE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => changeCategory(cat)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition",
                  activeCategory === cat
                    ? "bg-brand-surface font-semibold text-brand-dark"
                    : "text-slate-600 hover:bg-slate-100"
                )}
              >
                <span className="truncate">{cat}</span>
                <span
                  className={cn(
                    "ml-1 min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10px] tabular-nums",
                    activeCategory === cat
                      ? "bg-brand/10 font-bold text-brand"
                      : "bg-slate-200/70 text-slate-500"
                  )}
                >
                  {catCounts[cat] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* ── main content ── */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* search + mobile category */}
            <div className="border-b border-slate-100 bg-white px-4 py-2.5">
              <Input
                placeholder="Search by name..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => changeSearch(e.target.value)}
                allowClear
              />
              {/* mobile category pills */}
              <div className="mt-2 flex flex-wrap gap-1 md:hidden">
                {IMAGE_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => changeCategory(cat)}
                    className={cn(
                      "rounded-full px-2.5 py-1 text-[10px] font-semibold transition",
                      activeCategory === cat
                        ? "bg-brand text-white"
                        : "bg-slate-100 text-slate-500"
                    )}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* image area */}
            <div className="flex-1 overflow-y-auto bg-slate-50/30 p-3">
              {paged.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
                  <ImageIcon className="h-12 w-12" strokeWidth={1} />
                  <p className="text-sm">No images found</p>
                </div>
              ) : viewMode === "grid" ? (
                <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6">
                  {paged.map((img) => (
                    <div key={img.key} className="group relative">
                      <button
                        type="button"
                        onClick={() => {
                          onSelect(img.src);
                          onClose();
                        }}
                        className={cn(
                          "flex w-full flex-col items-center gap-1.5 rounded-xl border-2 bg-white p-2 transition hover:shadow-lg",
                          currentImage === img.src
                            ? "border-brand bg-brand-surface ring-2 ring-brand/20"
                            : "border-slate-200/60 hover:border-brand-muted"
                        )}
                      >
                        <div className="flex h-20 w-full items-center justify-center rounded-lg bg-gradient-to-br from-slate-50 to-slate-100/50">
                          <img
                            src={img.src}
                            alt={img.label}
                            className="h-16 w-16 object-contain drop-shadow-sm"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = "/products/other.svg";
                            }}
                          />
                        </div>
                        <span className="w-full truncate text-center text-[10px] font-medium text-slate-600">
                          {img.label}
                        </span>
                        <span className="w-full truncate text-center text-[9px] text-slate-400">
                          {img.category}
                        </span>
                      </button>
                      {/* preview zoom button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreview(img);
                        }}
                        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:bg-brand hover:text-white"
                      >
                        <ZoomIn className="h-3 w-3" />
                      </button>
                      {currentImage === img.src && (
                        <div className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white shadow">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                /* list view */
                <div className="space-y-1">
                  {paged.map((img) => (
                    <button
                      key={img.key}
                      type="button"
                      onClick={() => {
                        onSelect(img.src);
                        onClose();
                      }}
                      className={cn(
                        "group flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition hover:shadow-sm",
                        currentImage === img.src
                          ? "border-brand bg-brand-surface ring-1 ring-brand/20"
                          : "border-slate-200 bg-white hover:border-brand-muted"
                      )}
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-50 to-slate-100/50">
                        <img
                          src={img.src}
                          alt={img.label}
                          className="h-10 w-10 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = "/products/other.svg";
                          }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {img.label}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {img.src.split("/").pop()}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                        {img.category}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreview(img);
                        }}
                        className="shrink-0 rounded-lg p-1.5 text-slate-400 opacity-0 transition group-hover:opacity-100 hover:bg-slate-100 hover:text-brand"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </button>
                      {currentImage === img.src && (
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-white">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* pagination + URL footer */}
            <div className="border-t border-slate-200 bg-white px-4 py-2.5">
              {totalPages > 1 && (
                <div className="mb-2 flex items-center justify-center gap-1">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="flex h-7 items-center gap-1 rounded px-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                  >
                    <ChevronLeft className="h-3 w-3" />
                    Prev
                  </button>
                  {pageRange.map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`e${i}`}
                        className="px-1 text-xs text-slate-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        className={cn(
                          "h-7 w-7 rounded text-xs font-semibold transition",
                          p === page
                            ? "bg-brand text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100"
                        )}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="flex h-7 items-center gap-1 rounded px-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                  >
                    Next
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="mb-1 block text-[11px] font-semibold text-slate-500">
                    Or paste an image URL
                  </label>
                  <Input
                    placeholder="https://..."
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    size="small"
                  />
                </div>
                <Button
                  type="primary"
                  size="small"
                  disabled={!customUrl.trim()}
                  onClick={() => {
                    onSelect(customUrl.trim());
                    setCustomUrl("");
                    onClose();
                  }}
                >
                  Use URL
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Full preview overlay ── */}
      {preview && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setPreview(null)}
        >
          <div
            className="relative mx-4 flex max-h-[85vh] max-w-lg flex-col items-center rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex h-64 w-64 items-center justify-center rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 sm:h-80 sm:w-80">
              <img
                src={preview.src}
                alt={preview.label}
                className="max-h-full max-w-full object-contain drop-shadow-md"
              />
            </div>
            <h4 className="mt-4 text-lg font-bold text-slate-800">
              {preview.label}
            </h4>
            <p className="text-xs text-slate-400">
              {preview.src.split("/").pop()}
            </p>
            <div className="mt-1 flex items-center gap-1.5">
              <TagIcon className="h-3 w-3 text-brand" />
              <span className="text-xs font-medium text-brand">
                {preview.category}
              </span>
            </div>
            <div className="mt-5 flex gap-2">
              <Button
                type="primary"
                onClick={() => {
                  onSelect(preview.src);
                  setPreview(null);
                  onClose();
                }}
              >
                Use this image
              </Button>
              <Button onClick={() => setPreview(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ─── Product card ─── */
function ProductCard({ product, onEdit, onDelete, onToggle }) {
  const isActive = product.active ?? product.status;
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80 transition hover:shadow-xl hover:ring-brand/25">
      {/* top: image */}
      <div className="relative flex h-44 items-center justify-center overflow-hidden bg-gradient-to-b from-violet-50 to-white">
        {product.image ? (
          <img
            src={product.image}
            alt={product.product}
            className="h-32 w-32 object-contain drop-shadow transition duration-300 group-hover:scale-110"
            onError={(e) => {
              e.target.src = "/products/other.svg";
            }}
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-violet-100/50">
            <Package className="h-10 w-10 text-brand-muted/50" strokeWidth={1.2} />
          </div>
        )}

        {/* status badge */}
        <span
          className={cn(
            "absolute left-3 top-3 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm",
            isActive
              ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200"
              : "bg-slate-100 text-slate-400 ring-1 ring-slate-200"
          )}
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              isActive ? "bg-emerald-500" : "bg-slate-400"
            )}
          />
          {isActive ? "Active" : "Off"}
        </span>

        {/* discount */}
        {product.discount > 0 && (
          <span className="absolute right-3 top-3 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
            -{product.discount}%
          </span>
        )}

        {/* hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/80 opacity-0 backdrop-blur-[2px] transition-all duration-200 group-hover:opacity-100">
          <button
            type="button"
            onClick={() => onEdit(product)}
            className="flex h-9 items-center gap-1.5 rounded-xl bg-brand px-4 text-xs font-bold text-white shadow-lg shadow-brand/20 transition hover:bg-brand-dark"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete(product.id)}
            className="flex h-9 items-center gap-1.5 rounded-xl bg-white px-4 text-xs font-bold text-red-500 shadow-lg ring-1 ring-red-200 transition hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* bottom: info + actions */}
      <div className="flex flex-1 flex-col border-t border-slate-100 px-4 pb-3 pt-3">
        <p className="truncate text-sm font-bold capitalize leading-tight text-slate-900">
          {product.product}
        </p>
        <div className="mt-1.5 flex items-end justify-between">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-extrabold tabular-nums leading-none text-brand-dark">
              {product.price?.toFixed?.(2) ?? product.price}
            </span>
            <span className="text-[11px] font-semibold text-slate-400">AED</span>
          </div>
          <Switch
            size="small"
            checked={isActive}
            onChange={(checked) => onToggle(checked, product)}
            className={isActive ? "!bg-brand" : ""}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Inline Add / Edit drawer ─── */
function ProductForm({
  open,
  onClose,
  onSubmit,
  product,
  categories,
  selectedCategory,
  selectedService,
  isAdding,
}) {
  const [formData, setFormData] = useState({});
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(
        product
          ? { ...product, category: selectedCategory }
          : {
              product: "",
              price: "",
              discount: 0,
              expressPrice: 0,
              image: "",
              active: true,
              category: selectedCategory,
              description: "",
            }
      );
    }
  }, [open, product, selectedCategory]);

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!formData.product?.trim()) return message.error("Name is required");
    if (!formData.price || formData.price <= 0)
      return message.error("Price must be > 0");
    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={isAdding ? "New product" : "Edit product"}
        description={`${selectedService} · ${selectedCategory}`}
        width={560}
        className="border-l border-violet-200/60 bg-white"
        bodyClassName="space-y-5"
        footer={
          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Saving..." : isAdding ? "Add product" : "Save"}
            </Button>
          </div>
        }
      >
          {/* image */}
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setImagePickerOpen(true)}
              className={cn(
                "flex h-28 w-28 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed transition",
                formData.image
                  ? "border-brand bg-brand-surface"
                  : "border-slate-300 bg-slate-50 hover:border-brand-muted"
              )}
            >
              {formData.image ? (
                <img
                  src={formData.image}
                  alt="Selected"
                  className="h-20 w-20 rounded-xl object-contain"
                />
              ) : (
                <>
                  <ImageIcon
                    className="h-8 w-8 text-brand-muted"
                    strokeWidth={1.5}
                  />
                  <span className="text-[10px] font-semibold text-brand-muted">
                    Pick image
                  </span>
                </>
              )}
            </button>
            {formData.image && (
              <button
                type="button"
                className="text-xs text-red-500 hover:underline"
                onClick={() => set("image", "")}
              >
                Remove image
              </button>
            )}
          </div>

          {/* name */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              Product name *
            </label>
            <Input
              value={formData.product}
              onChange={(e) => set("product", e.target.value)}
              placeholder="e.g. Dress shirt"
            />
          </div>

          {/* category */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              Category
            </label>
            <Select
              value={formData.category}
              onChange={(v) => set("category", v)}
              className="w-full"
            >
              {categories.map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </div>

          {/* pricing */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">
                Price (AED) *
              </label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) =>
                  set("price", parseFloat(e.target.value) || "")
                }
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">
                Discount
              </label>
              <Input
                type="number"
                value={formData.discount}
                onChange={(e) =>
                  set("discount", parseFloat(e.target.value) || 0)
                }
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">
                Express
              </label>
              <Input
                type="number"
                value={formData.expressPrice}
                onChange={(e) =>
                  set("expressPrice", parseFloat(e.target.value) || 0)
                }
                placeholder="0"
              />
            </div>
          </div>

          {/* description */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              Description
            </label>
            <TextArea
              rows={3}
              value={formData.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          {/* status */}
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.active}
              onChange={(v) => set("active", v)}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
            <span className="text-sm text-slate-600">Product active</span>
          </div>
      </Drawer>

      <ImagePickerModal
        open={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onSelect={(src) => set("image", src)}
        currentImage={formData.image}
      />
    </>
  );
}

/* ─── Service pill with quick-edit & delete ─── */
function ServicePill({ name, selected, onSelect, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = draft.trim().toLowerCase();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      setDraft(name);
      return;
    }
    setSaving(true);
    try {
      await onRename(name, trimmed);
      setEditing(false);
    } catch {
      setDraft(name);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-brand/40 bg-brand-surface/60 px-1.5 py-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setEditing(false);
              setDraft(name);
            }
          }}
          className="!py-0.5 !text-xs"
          style={{ width: 110 }}
          autoFocus
          disabled={saving}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex h-6 w-6 items-center justify-center rounded text-brand hover:bg-brand/10"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(name);
          }}
          className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold capitalize transition",
        selected
          ? "bg-brand text-white shadow-md shadow-brand/25"
          : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-brand-surface/40 hover:ring-brand-muted"
      )}
    >
      <Package className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{name}</span>
      {selected && <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 opacity-60" />}
      {/* hover-only icons */}
      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
            setDraft(name);
          }}
          title="Rename"
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded transition",
            selected
              ? "text-white/60 hover:bg-white/20 hover:text-white"
              : "text-slate-400 hover:bg-slate-100 hover:text-brand"
          )}
        >
          <Pencil className="h-2.5 w-2.5" />
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(name);
          }}
          title="Delete"
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded transition",
            selected
              ? "text-white/60 hover:bg-white/20 hover:text-red-200"
              : "text-slate-400 hover:bg-red-50 hover:text-red-500"
          )}
        >
          <Trash2 className="h-2.5 w-2.5" />
        </span>
      </div>
    </button>
  );
}

/* ─── Inline add service (parent) ─── */
function InlineAddService({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const name = value.trim().toLowerCase();
    if (!name) return;
    setSaving(true);
    try {
      await onAdd(name);
      setValue("");
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-brand-muted/50 px-3 py-2 text-xs font-semibold text-brand-muted transition hover:border-brand hover:bg-brand-surface/60 hover:text-brand"
      >
        <Plus className="h-3.5 w-3.5" />
        Add service
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-brand/30 bg-brand-surface/50 p-2">
      <Input
        placeholder="e.g. dry cleaning"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        autoFocus
      />
      <div className="flex gap-1.5">
        <Button
          type="primary"
          onClick={submit}
          disabled={saving || !value.trim()}
          className="flex-1 !text-xs"
        >
          {saving ? "..." : "Create"}
        </Button>
        <Button
          onClick={() => {
            setOpen(false);
            setValue("");
          }}
          className="!text-xs"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ─── Category pill with quick-edit & delete ─── */
function CategoryPill({ name, selected, onSelect, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      setDraft(name);
      return;
    }
    setSaving(true);
    try {
      await onRename(name, trimmed);
      setEditing(false);
    } catch {
      setDraft(name);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 rounded-full border border-brand/40 bg-brand-surface/60 px-2 py-0.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setEditing(false);
              setDraft(name);
            }
          }}
          className="!py-0 !text-xs"
          style={{ width: 100 }}
          autoFocus
          disabled={saving}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex h-5 w-5 items-center justify-center rounded-full text-brand hover:bg-brand/10"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(name);
          }}
          className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative rounded-full px-4 py-1.5 text-xs font-semibold transition",
        selected
          ? "bg-brand-dark text-white shadow-sm"
          : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-brand-muted hover:text-brand-dark"
      )}
    >
      {name || "Other"}
      {/* hover-only icons */}
      <span className="absolute -right-1 -top-1 flex items-center gap-0.5 rounded-full bg-white p-0.5 opacity-0 shadow-sm ring-1 ring-slate-200 transition group-hover:opacity-100">
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
            setDraft(name);
          }}
          title="Rename"
          className="flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:bg-brand-surface hover:text-brand"
        >
          <Pencil className="h-2 w-2" />
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(name);
          }}
          title="Delete"
          className="flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 className="h-2 w-2" />
        </span>
      </span>
    </button>
  );
}

/* ─── Inline add category ─── */
function InlineAddCategory({ onAdd, existingCategories }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const name = value.trim();
    if (!name) return;
    if (existingCategories.includes(name)) {
      message.error("Category already exists");
      return;
    }
    setSaving(true);
    try {
      await onAdd(name);
      setValue("");
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 rounded-full border border-dashed border-brand-muted/50 px-3 py-1.5 text-[11px] font-semibold text-brand-muted transition hover:border-brand hover:bg-brand-surface/60 hover:text-brand"
      >
        <FolderPlus className="h-3 w-3" />
        Add category
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Input
        placeholder="e.g. Kids"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        className="!py-1 !text-xs"
        style={{ width: 140 }}
        autoFocus
      />
      <Button
        type="primary"
        onClick={submit}
        disabled={saving || !value.trim()}
        className="!px-2 !py-1 !text-[11px]"
      >
        {saving ? "..." : "Add"}
      </Button>
      <Button
        onClick={() => {
          setOpen(false);
          setValue("");
        }}
        className="!px-2 !py-1 !text-[11px]"
      >
        Cancel
      </Button>
    </div>
  );
}

/* ─── Empty state ─── */
function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-brand-muted/40 bg-gradient-to-br from-brand-surface/60 to-white px-6 py-16">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-surface text-brand">
        <Package className="h-8 w-8" strokeWidth={1.5} />
      </div>
      <p className="max-w-xs text-center text-sm text-slate-600">
        No products in this category yet. Add your first one to get started.
      </p>
      <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
        Add product
      </Button>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
const Products = () => {
  const { ProductsTwo, fetchProduct, Parent, Category } =
    useContext(AuthContext);
  const [data, setData] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchText, setSearchText] = useState("");
  const [productPage, setProductPage] = useState(1);
  const PRODUCTS_PER_PAGE = 12;

  // form
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  /* derived */
  const serviceNames = useMemo(
    () => data?.map((d) => d.name) || [],
    [data]
  );
  const serviceData = useMemo(
    () => data?.find((d) => d.name === selectedService),
    [data, selectedService]
  );
  const categories = useMemo(
    () => (serviceData ? Object.keys(serviceData.categories) : []),
    [serviceData]
  );
  const products = useMemo(() => {
    if (!serviceData?.categories?.[selectedCategory]) return [];
    let items = serviceData.categories[selectedCategory];
    if (searchText) {
      const q = searchText.toLowerCase();
      items = items.filter((p) => p.product.toLowerCase().includes(q));
    }
    return items;
  }, [serviceData, selectedCategory, searchText]);

  /* bootstrap */
  useEffect(() => {
    if (ProductsTwo?.length) {
      setData(ProductsTwo);
    } else {
      fetchProductsFromApi();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ProductsTwo?.length) setData(ProductsTwo);
  }, [ProductsTwo]);

  useEffect(() => {
    if (serviceNames.length && !selectedService)
      setSelectedService(serviceNames[0]);
  }, [serviceNames, selectedService]);

  useEffect(() => {
    if (categories.length && !categories.includes(selectedCategory))
      setSelectedCategory(categories[0]);
  }, [categories, selectedCategory]);

  useEffect(() => {
    setProductPage(1);
  }, [selectedCategory, selectedService, searchText]);

  const fetchProductsFromApi = async () => {
    try {
      const res = await ApiPath.get("/products");
      setData(res.data);
    } catch {
      message.error("Failed to load products");
    }
  };

  /* ── add service (parent) ── */
  const handleAddService = async (name) => {
    try {
      await ApiPath.post("/products", [{ name, categories: {} }]);
      message.success(`Service "${name}" created`);
      await fetchProductsFromApi();
      setSelectedService(name);
    } catch {
      message.error("Failed to create service");
    }
  };

  /* ── add category to current service ── */
  const handleAddCategory = async (categoryName) => {
    if (!serviceData) return message.error("Select a service first");
    try {
      const updated = { ...serviceData };
      if (updated.categories instanceof Map) {
        updated.categories = Object.fromEntries(updated.categories);
      }
      updated.categories[categoryName] = [];
      await ApiPath.put(`/products/${serviceData._id}`, updated);
      message.success(`Category "${categoryName}" added`);
      await fetchProductsFromApi();
      setSelectedCategory(categoryName);
    } catch {
      message.error("Failed to add category");
    }
  };

  /* ── rename service ── */
  const handleRenameService = async (oldName, newName) => {
    const svc = data.find((d) => d.name === oldName);
    if (!svc) return;
    try {
      await ApiPath.put(`/products/${svc._id}`, { ...svc, name: newName });
      message.success(`Service renamed to "${newName}"`);
      if (selectedService === oldName) setSelectedService(newName);
      await fetchProductsFromApi();
    } catch {
      message.error("Failed to rename service");
      throw new Error("rename failed");
    }
  };

  /* ── delete service ── */
  const handleDeleteService = (name) => {
    const svc = data.find((d) => d.name === name);
    if (!svc) return;
    Modal.confirm({
      title: `Delete service "${name}"?`,
      content:
        "This will remove the service and all its categories & products permanently.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await ApiPath.delete(`/products/${svc._id}`);
          message.success(`Service "${name}" deleted`);
          if (selectedService === name) setSelectedService("");
          await fetchProductsFromApi();
        } catch {
          message.error("Failed to delete service");
        }
      },
    });
  };

  /* ── rename category ── */
  const handleRenameCategory = async (oldName, newName) => {
    if (!serviceData) return;
    try {
      const cats = { ...serviceData.categories };
      if (cats instanceof Map) {
        Object.assign(cats, Object.fromEntries(cats));
      }
      if (cats[newName]) {
        message.error(`Category "${newName}" already exists`);
        throw new Error("duplicate");
      }
      cats[newName] = cats[oldName];
      delete cats[oldName];
      await ApiPath.put(`/products/${serviceData._id}`, {
        ...serviceData,
        categories: cats,
      });
      message.success(`Category renamed to "${newName}"`);
      if (selectedCategory === oldName) setSelectedCategory(newName);
      await fetchProductsFromApi();
    } catch (err) {
      if (err.message !== "duplicate") message.error("Failed to rename category");
      throw err;
    }
  };

  /* ── delete category ── */
  const handleDeleteCategory = (catName) => {
    if (!serviceData) return;
    const count = serviceData.categories?.[catName]?.length || 0;
    Modal.confirm({
      title: `Delete category "${catName}"?`,
      content: count
        ? `This will remove ${count} product${count > 1 ? "s" : ""} in this category.`
        : "This empty category will be removed.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const cats = { ...serviceData.categories };
          delete cats[catName];
          await ApiPath.put(`/products/${serviceData._id}`, {
            ...serviceData,
            categories: cats,
          });
          message.success(`Category "${catName}" deleted`);
          if (selectedCategory === catName) setSelectedCategory("");
          await fetchProductsFromApi();
        } catch {
          message.error("Failed to delete category");
        }
      },
    });
  };

  /* actions */
  const openAdd = () => {
    setEditingProduct(null);
    setIsAdding(true);
    setDrawerOpen(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setIsAdding(false);
    setDrawerOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete product?",
      content: "This will remove the product permanently.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await ApiPath.delete(`/products/${id}`);
          message.success("Product deleted");
          fetchProductsFromApi();
        } catch {
          message.error("Delete failed");
        }
      },
    });
  };

  const handleToggle = async (checked, record) => {
    try {
      await ApiPath.put("/products/update-product", {
        categoryKey: selectedCategory,
        productId: record.id,
        status: checked,
      });
      message.success(
        `Product ${checked ? "activated" : "deactivated"}`
      );
      fetchProductsFromApi();
    } catch {
      message.error("Status update failed");
    }
  };

  const handleSubmit = async (values) => {
    if (isAdding) {
      await ApiPath.post("/products", {
        ...values,
        name: selectedService,
        category: values.category,
      });
      message.success("Product added");
    } else {
      await ApiPath.put("/products/update-product", {
        categoryKey: values.category,
        productId: values.id,
        newPrice: values.price,
        product: values.product,
      });
      message.success("Product updated");
    }
    fetchProductsFromApi();
  };

  /* stat counters */
  const totalProducts = useMemo(() => {
    if (!serviceData?.categories) return 0;
    return Object.values(serviceData.categories).reduce(
      (s, arr) => s + arr.length,
      0
    );
  }, [serviceData]);

  return (
    <PageStack>
      <PageHeader
        title="Products"
        description="Browse, add and manage products across all services and categories."
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add product
          </Button>
        }
      />

      {/* ── KPI mini-strip ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex items-center gap-3 rounded-xl border border-violet-200/60 bg-white p-3 shadow-card ring-1 ring-brand/10">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-surface text-brand">
            <Layers className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-xl font-extrabold tabular-nums text-slate-900">
              {serviceNames.length}
            </p>
            <p className="text-[11px] font-medium text-slate-500">Services</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-violet-200/60 bg-white p-3 shadow-card ring-1 ring-brand/10">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-surface text-brand">
            <Grid3X3 className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-xl font-extrabold tabular-nums text-slate-900">
              {categories.length}
            </p>
            <p className="text-[11px] font-medium text-slate-500">
              Categories
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-violet-200/60 bg-white p-3 shadow-card ring-1 ring-brand/10">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-surface text-brand">
            <Package className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-xl font-extrabold tabular-nums text-slate-900">
              {totalProducts}
            </p>
            <p className="text-[11px] font-medium text-slate-500">
              In service
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-violet-200/60 bg-white p-3 shadow-card ring-1 ring-brand/10">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-surface text-brand">
            <Sparkles className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-xl font-extrabold tabular-nums text-slate-900">
              {products.length}
            </p>
            <p className="text-[11px] font-medium text-slate-500">Showing</p>
          </div>
        </div>
      </div>

      {/* ── Service + Category nav ── */}
      <div className="flex flex-col gap-4 lg:flex-row">
        {/* service pills */}
        <div className="shrink-0 lg:w-48">
          <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Services
          </p>
          <div className="flex flex-wrap gap-1.5 lg:flex-col">
            {serviceNames.map((name) => (
              <ServicePill
                key={name}
                name={name}
                selected={selectedService === name}
                onSelect={() => setSelectedService(name)}
                onRename={handleRenameService}
                onDelete={handleDeleteService}
              />
            ))}
            <InlineAddService onAdd={handleAddService} />
          </div>
        </div>

        {/* main area */}
        <div className="min-w-0 flex-1">
          {/* category bar + search */}
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-1.5">
              {categories.map((cat) => (
                <CategoryPill
                  key={cat}
                  name={cat}
                  selected={selectedCategory === cat}
                  onSelect={() => setSelectedCategory(cat)}
                  onRename={handleRenameCategory}
                  onDelete={handleDeleteCategory}
                />
              ))}
              {selectedService && (
                <InlineAddCategory
                  onAdd={handleAddCategory}
                  existingCategories={categories}
                />
              )}
            </div>
            <div className="w-full max-w-xs">
              <Input
                placeholder="Search products..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {/* products grid */}
          {(() => {
            const totalProductPages = Math.ceil(products.length / PRODUCTS_PER_PAGE);
            const pagedProducts = products.slice(
              (productPage - 1) * PRODUCTS_PER_PAGE,
              productPage * PRODUCTS_PER_PAGE
            );

            if (products.length === 0) return <EmptyState onAdd={openAdd} />;

            return (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {pagedProducts.map((p) => (
                    <ProductCard
                      key={p.id}
                      product={p}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  ))}
                  {/* add card on last page */}
                  {productPage === totalProductPages && (
                    <button
                      type="button"
                      onClick={openAdd}
                      className="flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-brand-muted/30 bg-gradient-to-br from-brand-surface/30 to-white text-brand-muted transition hover:border-brand hover:text-brand hover:shadow-lg"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-surface/80">
                        <Plus className="h-6 w-6" />
                      </div>
                      <span className="text-sm font-semibold">Add product</span>
                    </button>
                  )}
                </div>

                {/* pagination — always visible */}
                <div className="mt-5 flex items-center justify-between rounded-xl border border-slate-200/70 bg-white px-4 py-3 shadow-sm">
                  <p className="text-xs text-slate-500">
                    Showing{" "}
                    <span className="font-bold text-slate-800">
                      {(productPage - 1) * PRODUCTS_PER_PAGE + 1}–
                      {Math.min(productPage * PRODUCTS_PER_PAGE, products.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-bold text-slate-800">{products.length}</span>{" "}
                    products
                  </p>
                  {totalProductPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={productPage <= 1}
                        onClick={() => setProductPage((p) => p - 1)}
                        className="flex h-8 items-center gap-1 rounded-lg px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-30"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        Prev
                      </button>
                      {Array.from({ length: totalProductPages }, (_, i) => i + 1).map(
                        (pg) => (
                          <button
                            key={pg}
                            type="button"
                            onClick={() => setProductPage(pg)}
                            className={cn(
                              "flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold transition",
                              pg === productPage
                                ? "bg-brand text-white shadow-sm"
                                : "text-slate-600 hover:bg-slate-100"
                            )}
                          >
                            {pg}
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        disabled={productPage >= totalProductPages}
                        onClick={() => setProductPage((p) => p + 1)}
                        className="flex h-8 items-center gap-1 rounded-lg px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-30"
                      >
                        Next
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Slide-over form */}
      <ProductForm
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        product={editingProduct}
        categories={categories}
        selectedCategory={selectedCategory}
        selectedService={selectedService}
        isAdding={isAdding}
      />
    </PageStack>
  );
};

export default Products;
